import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { toRpcSpec } from '@/dashboard/lib/to-rpc-spec';

import type { ChatDashboardArtifactsStore, DashboardArtifact } from './useChatDashboardArtifacts';
import { isArtifactAutoSaveEligible } from './useChatDashboardArtifacts';

/** Coalesce streaming agent patches into one write (matches routine editor autosave). */
const AUTOSAVE_DEBOUNCE_MS = 1000;

export interface DashboardArtifactAutoSaveControls {
  /** Cancel pending debounced/in-flight bookkeeping (e.g. on chat session switch). */
  cancelPending: () => void;
}

export interface DashboardArtifactAutoSaveOptions {
  /**
   * Defaults to true. Pass false in a read-only chat: `dashboard.save` is workspace-scoped, not
   * chat-owner-scoped, so unlike every other chat write it would NOT be rejected — a viewer whose
   * restored artifacts looked dirty could silently write (and meter) a dashboard from a chat they
   * don't own.
   */
  enabled?: boolean;
}

/**
 * Persist agent-driven edits to already-saved dashboards (free update).
 * Runs at conversation level so auto-save continues when the preview panel is closed.
 */
export function useDashboardArtifactAutoSave(
  artifacts: Record<string, DashboardArtifact>,
  store: Pick<ChatDashboardArtifactsStore, 'markAutoSaved'>,
  chatSessionId: string,
  { enabled = true }: DashboardArtifactAutoSaveOptions = {},
): DashboardArtifactAutoSaveControls {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const artifactsRef = useRef(artifacts);
  const chatSessionIdRef = useRef(chatSessionId);
  useLayoutEffect(() => {
    artifactsRef.current = artifacts;
  }, [artifacts]);
  useLayoutEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  const inFlightRef = useRef(new Set<string>());
  const flushedPatchSeqRef = useRef(new Map<string, number>());
  const debounceTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const lastScheduledSeqRef = useRef(new Map<string, number>());
  // Per-slug patchSeq we've already retried once after a failed save — bounds retries to one-per-seq so
  // a persistently-failing save can't storm (a new agent patch advances patchSeq and re-arms one retry).
  const retriedSeqRef = useRef(new Map<string, number>());
  const scheduleAutoSaveRef = useRef<(slug: string) => void>(() => undefined);

  const cancelPending = useCallback((): void => {
    for (const handle of debounceTimersRef.current.values()) clearTimeout(handle);
    debounceTimersRef.current.clear();
    inFlightRef.current.clear();
    flushedPatchSeqRef.current.clear();
    lastScheduledSeqRef.current.clear();
    retriedSeqRef.current.clear();
  }, []);

  /* eslint-disable react-hooks/refs -- autosave mutation callbacks read refs only when async handlers run */
  const saveMutation = useMutation(
    orpc.dashboard.save.mutationOptions({
      onSuccess: (result, variables) => {
        if (variables.chatSessionId !== chatSessionIdRef.current) {
          flushedPatchSeqRef.current.delete(variables.slug);
          return;
        }

        const dashboard = result.dashboard;
        const flushedSeq = flushedPatchSeqRef.current.get(dashboard.slug);
        if (flushedSeq !== undefined) {
          store.markAutoSaved(dashboard.slug, flushedSeq);
          flushedPatchSeqRef.current.delete(dashboard.slug);
        }

        void queryClient.invalidateQueries({
          queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err, variables) => {
        const failedSeq = flushedPatchSeqRef.current.get(variables.slug);
        flushedPatchSeqRef.current.delete(variables.slug);
        // Bounded single retry for transient failures (e.g. a 5xx): retry a given patchSeq at most once.
        // The retry rides the normal debounce path (fires after onSettled clears `inFlightRef`); if it
        // fails again the guard below (same failedSeq) stops it, so a persistent error can't storm.
        if (
          failedSeq !== undefined &&
          variables.chatSessionId === chatSessionIdRef.current &&
          retriedSeqRef.current.get(variables.slug) !== failedSeq
        ) {
          retriedSeqRef.current.set(variables.slug, failedSeq);
          scheduleAutoSaveRef.current(variables.slug);
        }
        // Stable per-slug id so a persistently-failing auto-save updates one toast instead of
        // stacking a new toast on every retry (toast storm during agent streaming).
        toast.error(err.message || 'Failed to auto-save dashboard', {
          id: `dashboard-autosave-error-${variables.slug}`,
        });
      },
      onSettled: (_result, error, variables) => {
        inFlightRef.current.delete(variables.slug);
        if (error) {
          flushedPatchSeqRef.current.delete(variables.slug);
          return;
        }
        // Successful save clears any retry debt for this slug so a later failure at a NEW seq can retry.
        retriedSeqRef.current.delete(variables.slug);

        if (variables.chatSessionId !== chatSessionIdRef.current) {
          flushedPatchSeqRef.current.delete(variables.slug);
          return;
        }

        // Read the latest artifacts map (layout effect keeps the ref current). Use the shared
        // eligibility predicate so this reschedule respects the in-flight-persist gate too (a bare
        // saved+dirty check here would race a fresh update_dashboard persist).
        const artifact = artifactsRef.current[variables.slug];
        if (artifact && isArtifactAutoSaveEligible(artifact)) {
          scheduleAutoSaveRef.current(variables.slug);
        }
      },
    }),
  );
  /* eslint-enable react-hooks/refs -- end autosave mutation block */

  const mutateRef = useRef(saveMutation.mutate);
  useLayoutEffect(() => {
    mutateRef.current = saveMutation.mutate;
  }, [saveMutation.mutate]);

  const enabledRef = useRef(enabled);
  useLayoutEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const flushAutoSave = useCallback((slug: string, scheduledForSessionId: string): void => {
    if (!enabledRef.current) return;
    if (scheduledForSessionId !== chatSessionIdRef.current) return;

    const artifact = artifactsRef.current[slug];
    // Eligible = saved + not mid server-persist + has newer local edits. `isArtifactSaving`
    // (pendingPersistPatchSeq set) keeps auto-save off while an update_dashboard persist is in flight.
    if (!artifact || !isArtifactAutoSaveEligible(artifact)) return;
    if (inFlightRef.current.has(slug)) return;

    inFlightRef.current.add(slug);
    flushedPatchSeqRef.current.set(slug, artifact.patchSeq);
    const rpcSpec = toRpcSpec(artifact.spec);
    mutateRef.current({
      id: artifact.saved.id,
      title: artifact.title,
      description: artifact.description ?? null,
      slug: artifact.slug,
      warehouseId: artifact.warehouseId,
      spec: rpcSpec,
      chatSessionId: scheduledForSessionId,
    });
  }, []);

  const scheduleAutoSave = useCallback(
    (slug: string): void => {
      const sessionIdAtSchedule = chatSessionIdRef.current;
      const existing = debounceTimersRef.current.get(slug);
      if (existing) clearTimeout(existing);
      debounceTimersRef.current.set(
        slug,
        setTimeout(() => {
          debounceTimersRef.current.delete(slug);
          flushAutoSave(slug, sessionIdAtSchedule);
        }, AUTOSAVE_DEBOUNCE_MS),
      );
    },
    [flushAutoSave],
  );

  useLayoutEffect(() => {
    scheduleAutoSaveRef.current = scheduleAutoSave;
  }, [scheduleAutoSave]);

  useEffect(() => {
    if (!enabled) return;
    for (const artifact of Object.values(artifacts)) {
      if (!isArtifactAutoSaveEligible(artifact)) continue;
      // Only (re)schedule a slug when ITS spec actually advanced. Rescheduling every eligible slug on
      // any map change lets continuous streaming to one dashboard keep resetting the debounce of every
      // other dirty dashboard, starving their auto-save (they'd never reach the flush).
      if (lastScheduledSeqRef.current.get(artifact.slug) === artifact.patchSeq) continue;
      lastScheduledSeqRef.current.set(artifact.slug, artifact.patchSeq);
      scheduleAutoSave(artifact.slug);
    }
  }, [artifacts, scheduleAutoSave, enabled]);

  useEffect(() => {
    const timers = debounceTimersRef.current;
    return () => {
      for (const handle of timers.values()) clearTimeout(handle);
      timers.clear();
    };
  }, []);

  // Drop pending work when the chat session changes (ChatConversation is reused across /chat/:id).
  const prevSessionIdRef = useRef(chatSessionId);
  useEffect(() => {
    if (chatSessionId === prevSessionIdRef.current) return;
    prevSessionIdRef.current = chatSessionId;
    cancelPending();
  }, [chatSessionId, cancelPending]);

  return { cancelPending };
}
